/* eslint-disable max-lines-per-function */
import LoadingUI from 'extensions/loading-ui';
import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Ajax Login Register
 *--------------------------------------------------------------------------
 *
 * Interactive functionality for login and register forms.
 *
 */
export default class AjaxLoginRegister {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Root container element
	 *
	 * @var {HTMLElement}
	 */
	container;

	/**
	 * Response message element
	 *
	 * @var {HTMLElement}
	 */
	messageElement;

	/**
	 * List of form elements
	 *
	 * @var {NodeList}
	 */
	formElements;

	/**
	 * Loader class instance
	 *
	 * @var {LoadingUI}
	 */
	loader;

	/**
	 * The loader element
	 *
	 * @var {HTMLElement}
	 */
	loaderElement;

	/**
	 * Attribute to explicitly set the window location on successful form submission
	 *
	 * @var {String}
	 */
	locationAttribute;

	/**
	 * Primary form toggle elements object
	 *
	 * @prop {HTMLElement}  control  The primary form control element
	 * @prop {HTMLElement}  element  The primary form container element
	 * @prop {String|Null}  headingTitle  The primary form heading title toggle
	 */
	primaryToggleElements;

	/**
	 * Secondary form toggle elements object
	 *
	 * @prop {HTMLElement}  control  The secondary form control element
	 * @prop {HTMLElement}  element  The secondary form container element
	 * @prop {String|Null}  headingTitle  The secondary form heading title toggle
	 */
	secondaryToggleElements;

	/**
	 * Class to apply to the active form toggle container element
	 *
	 * @var {String}
	 */
	toggleFormOpenClass;

	/**
	 * Heading element to target for updating text content based on the active form toggle
	 *
	 * @var {HTMLElement}
	 */
	toggleFormHeadingElement;

	/**
	 *  Reference to a function to be called before the form submission event
	 *
	 * @var {Function|Null}
	 */
	beforeSubmit;

	/**
	 * Create a AjaxLoginRegister instance
	 *
	 * @param  {HTMLElement|String}              container                                  The root container element.
	 * @param  {Function|Null}                   options.beforeSubmit                       Reference to a function to be called before the form submission event. Defaults to null.
	 * @param  {HTMLCollection|NodeList|String}  options.formElements                       The form elements. Defaults to '.js-login-register-form'.
	 * @param  {HTMLElement|String}              options.formMessageElement                 The element to target for displaying a form element's network response messages. This must be a child element of each form element. Defaults to '.js-login-register-message'.
	 * @param  {HTMLElement|String}              options.loaderElement                      The loading state element. Defaults to '#js-loading-container'.
	 * @param  {String}                          options.locationAttribute                  The attribute name to explicitly set the window location on successful form submission. This attribute must be added to the form element and contain a URL value or value of "action" to use the form element's action value. Excluding this attribute from the form element will result in a reload of the current window location on successful form submission. Defaults to 'data-location'.
	 * @param  {Boolean}                         options.toggleFormEnabled                  Enable the form toggle functionality. Defaults to true.
	 * @param  {String}                          options.toggleFormHeadingAttribute         The attribute name to target for the heading text update on form toggle. Set to an empty string literal '' to disable the feature. Defaults to 'data-heading'.
	 * @param  {HTMLElement|String}              options.toggleFormHeadingElement           Heading element to target for updating text content based on the active form toggle. Defaults to '#js-login-register-title'.
	 * @param  {String}                          options.toggleFormOpenClass                Class to apply to the active form toggle container element. Defaults to 'login-register__toggle--open'.
	 * @param  {HTMLElement|String}              options.toggleFormPrimaryControlElement    The primary form toggle control element. Defaults to '#js-login-register-primary-toggle-control'.
	 * @param  {HTMLElement|String}              options.toggleFormPrimaryElement           The primary form toggle container element. Defaults to '#js-login-register-primary-toggle'.
	 * @param  {HTMLElement|String}              options.toggleFormSecondaryControlElement  The secondary form toggle control element. Defaults to '#js-login-register-secondary-toggle-control'.
	 * @param  {HTMLElement|String}              options.toggleFormSecondaryElement         The secondary form toggle container element. Defaults to '#js-login-register-secondary-toggle'.
	 */
	constructor (container, {
		beforeSubmit = null,
		formElements = '.js-login-register-form',
		formMessageElement = '.js-login-register-message',
		loaderElement = '#js-loading-container',
		locationAttribute = 'data-location',
		toggleFormEnabled = true,
		toggleFormHeadingAttribute = 'data-heading',
		toggleFormHeadingElement = '#js-login-register-title',
		toggleFormOpenClass = 'login-register__toggle--open',
		toggleFormPrimaryControlElement = '#js-login-register-primary-toggle-control',
		toggleFormPrimaryElement = '#js-login-register-primary-toggle',
		toggleFormSecondaryControlElement = '#js-login-register-secondary-toggle-control',
		toggleFormSecondaryElement = '#js-login-register-secondary-toggle'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.container = this.#validator.isElement('container', container);

		this.formElements = this.#validator.isElementList('formElements', formElements, {container: this.container});

		for (const formElement of this.formElements) {
			formElement.messageElement = this.#validator.isElement('formMessageElement', formMessageElement, {container: formElement});
		}

		this.beforeSubmit = beforeSubmit;
		this.locationAttribute = this.#validator.isString('locationAttribute', locationAttribute);

		this.loader = null;
		this.loaderElement = this.#validator.isElement('loaderElement', loaderElement, {
			container: this.container,
			required: false
		});

		if (this.loaderElement) {
			this.loader = new LoadingUI(this.loaderElement, {
				overlayElement: this.container
			});
		}

		this.primaryToggleElements = {};
		this.secondaryToggleElements = {};
		this.toggleFormOpenClass = this.#validator.isString('toggleFormOpenClass', toggleFormOpenClass);
		this.toggleFormHeadingElement = this.#validator.isElement('toggleFormHeadingElement', toggleFormHeadingElement, {
			container: this.container,
			required: false
		});
		this.#validator.isBoolean('toggleFormEnabled', toggleFormEnabled);

		if (toggleFormEnabled) {
			this.primaryToggleElements = {
				control: this.#validator.isElement('toggleFormPrimaryControlElement', toggleFormPrimaryControlElement, {container: this.container}),
				element: this.#validator.isElement('toggleFormPrimaryElement', toggleFormPrimaryElement, {container: this.container})
			};

			this.secondaryToggleElements = {
				control: this.#validator.isElement('toggleFormSecondaryControlElement', toggleFormSecondaryControlElement, {container: this.container}),
				element: this.#validator.isElement('toggleFormSecondaryElement', toggleFormSecondaryElement, {container: this.container})
			};

			if (toggleFormHeadingAttribute !== '') {
				this.primaryToggleElements.headingTitle = this.primaryToggleElements.control.getAttribute(this.#validator.isString('toggleFormHeadingAttribute', toggleFormHeadingAttribute));
				this.secondaryToggleElements.headingTitle = this.secondaryToggleElements.control.getAttribute(this.#validator.isString('toggleFormHeadingAttribute', toggleFormHeadingAttribute));
			}

			this.initFormToggleElements();
		}

		this.bindEvents();
	}

	/**
	 * Initialize the form toggle functionality
	 */
	initFormToggleElements () {
		this.primaryToggleElements.control.addEventListener('click', (event) => {
			event.preventDefault();

			this.toggleForms(this.primaryToggleElements, this.secondaryToggleElements);
		});

		this.secondaryToggleElements.control.addEventListener('click', (event) => {
			event.preventDefault();

			this.toggleForms(this.secondaryToggleElements, this.primaryToggleElements);
		});
	}

	/**
	 * Toggle the primary and secondary forms
	 *
	 * @param  {Object} showToggleElements The toggle elements to show
	 * @param  {Object} hideToggleElements The toggle elements to hide
	 */
	toggleForms (showToggleElements, hideToggleElements) {
		hideToggleElements.element.classList.remove(this.toggleFormOpenClass);
		hideToggleElements.control.setAttribute('aria-expanded', 'false');
		hideToggleElements.control.classList.remove('u-hidden');
		hideToggleElements.control.focus();

		showToggleElements.control.classList.add('u-hidden');
		showToggleElements.control.setAttribute('aria-expanded', 'true');
		showToggleElements.element.classList.add(this.toggleFormOpenClass);

		if (showToggleElements.headingTitle && this.toggleFormHeadingElement) {
			this.toggleFormHeadingElement.textContent = showToggleElements.headingTitle;
		}
	}

	/**
	 * Bind the form events
	 */
	bindEvents () {
		for (const formElement of this.formElements) {
			formElement.addEventListener('submit', this.onSubmit.bind(this));
		}
	}

	/**
	 * Form submission handling
	 *
	 * @param  {Event} event Form submission event
	 */
	async onSubmit (event) {
		event.preventDefault();
		event.stopImmediatePropagation();

		const formElement = event.target;
		const formData = new FormData(formElement);
		const submitElement = formElement.querySelector('[type="submit"]');
		let payload = {
			errorMessage: '',
			formData: formData,
			formElement: formElement,
			returnPageCode: ''
		};

		submitElement.disabled = true;
		formElement.messageElement.innerHTML = '';

		if (this.loader) {
			this.loader.show();
		}

		// Run before submit function if defined
		if (typeof this.beforeSubmit === 'function') {
			this.beforeSubmit(this, event);
		}

		const response = await fetch(formElement.action, {
			body: formData,
			method: formElement.method || 'POST'
		});

		payload.responseHeaderPageCode = response.headers.get('g-mm-page-code');
		submitElement.disabled = false;

		if (!response.ok) {
			this.onFail(payload);

			throw Error(response.statusText);
		}

		const responseHtml = await response.text();
		const responseDom = new DOMParser().parseFromString(responseHtml, 'text/html');
		const isLoggedIn = response.headers.get('g-mm-cust-sess') ?? false;

		payload.errorMessage = responseDom.querySelector('.g-messages--error') ?? '';

		// eslint-disable-next-line no-extra-parens
		if (isLoggedIn || (!payload.errorMessage && payload.responseHeaderPageCode === window.mivaJS.Page_Code)) {
			this.onSuccess(formElement);
		} else {
			this.onFail(payload);
		}
	}

	/**
	 * Successful login / register response functionality
	 */
	onSuccess (formElement) {
		let locationValue = formElement.getAttribute(this.locationAttribute);

		if (locationValue) {
			if (locationValue.toLowerCase() === 'action') {
				locationValue = formElement.action;
			}

			window.location = locationValue;
		} else {
			window.location.reload();
		}
	}

	/**
	 * Unsuccessful login / register response functionality
	 *
	 * @param  {Object} payload The response payload object
	 */
	onFail (payload) {
		let errorMessage = payload.errorMessage && payload.errorMessage.outerHTML ? payload.errorMessage.outerHTML : '';

		if (!errorMessage) {
			if (!this.defaultErrorMessage) {
				this.defaultErrorMessage = document.createElement('div');

				this.defaultErrorMessage.classList.add('g-messages', 'g-messages--error');
				this.defaultErrorMessage.textContent = 'Unable to process form.';
			}

			errorMessage = this.defaultErrorMessage.outerHTML;
		}

		payload.formElement.messageElement.innerHTML = errorMessage;

		if (this.loader) {
			this.loader.hide();
		}
	}
}
